type DownloadWriter = {
  write(data: Uint8Array): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
};

type DownloadFile = {
  createWritable(): Promise<DownloadWriter>;
};

type SavePicker = (options: { suggestedName: string }) => Promise<DownloadFile>;

export async function downloadSavedVideo(adId: string, proxyUrl: string, fileName: string): Promise<void> {
  const grant = await fetch(`/api/generated-ads/${encodeURIComponent(adId)}/video-download-grant?filename=${encodeURIComponent(fileName)}`);
  if (grant.status === 409) {
    // Only non-R2 saved media takes the existing authenticated proxy path.
    return downloadVideoToDisk(proxyUrl, fileName);
  }
  await assertDownloadOk(grant);
  const { url, sizeBytes } = await grant.json();
  if (typeof url !== "string" || !url.startsWith("https://") ||
      !Number.isSafeInteger(sizeBytes) || sizeBytes <= 0) {
    throw new Error("Video download could not be prepared. Please try again.");
  }
  // Content-Disposition on the short-lived R2 GET supplies the filename.
  // Native download managers stream to disk and flag an interrupted transfer.
  const anchor = document.createElement("a");
  anchor.href = url;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export async function downloadVideoToDisk(proxyUrl: string, fileName: string): Promise<void> {
  const picker = (window as Window & { showSaveFilePicker?: SavePicker }).showSaveFilePicker;
  if (!picker) {
    // Native downloads stream to the browser's download manager. Probe the
    // authenticated proxy first so missing/expired media still raises a toast
    // rather than navigating the current tab to an error response.
    const probe = await fetch(proxyUrl, { method: "HEAD" });
    await assertDownloadOk(probe);
    const anchor = document.createElement("a");
    anchor.href = proxyUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return;
  }

  // The picker requires the original click's user activation: do not await
  // the network before showing it.
  let file: DownloadFile;
  try {
    file = await picker.call(window, { suggestedName: fileName });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return;
    throw error;
  }
  const response = await fetch(proxyUrl);
  await assertDownloadOk(response);
  if (!response.body) throw new Error("Download stream unavailable. Please try again.");
  const writer = await file.createWritable();
  const reader = response.body.getReader();
  let bytes = 0;
  const header = response.headers.get("content-length");
  const expected = header !== null && /^\d+$/.test(header) ? Number(header) : null;
  try {
    while (true) {
      const part = await reader.read();
      if (part.done) break;
      bytes += part.value.byteLength;
      if (expected !== null && bytes > expected) throw new Error("Download was interrupted. Please try again.");
      await writer.write(part.value);
    }
    if (expected !== null && bytes !== expected) throw new Error("Download was interrupted. Please try again.");
    // File System Access writes to a temporary file until close; abort drops
    // partial bytes instead of presenting an interrupted download as complete.
    await writer.close();
  } catch (error) {
    await writer.abort().catch(() => {});
    throw new Error("Download interrupted or connection lost. Please try again.", { cause: error });
  } finally {
    await reader.cancel().catch(() => {});
  }
}

async function assertDownloadOk(response: Response): Promise<void> {
  if (response.ok) return;
  if (response.status === 401) throw new Error("Your session expired. Please sign in and try again.");
  const detail = (await response.text()).slice(0, 160);
  throw new Error(detail || `Download failed (${response.status})`);
}